import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';
import { API, Badge, tableStyle, thStyle, tdStyle, btnPrimary, btnSecondary, btnDanger, inputStyle, overlayStyle, modalStyle, FormRow } from './RpShared';

const SCOPE_LABELS = { company: 'Company', department: 'Department', module: 'Module', submodule: 'Submodule' };

export default function RpGrants() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [grants, setGrants] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [scopeFilter, setScopeFilter] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ scope_type: 'company', scope_id: '', module_key: '', submodule_name: '', permission_id: '', state: 'grant', reason: '', is_temporary: false, starts_at: '', ends_at: '' });

  const fetchGrants = useCallback(async () => {
    setLoading(true);
    try {
      const url = new URL(`${API}/companies/${companyId}/roles-permissions/grants`);
      if (scopeFilter) url.searchParams.set('scope_type', scopeFilter);
      const res = await fetch(url, { headers: getAuthHeaders() });
      setGrants(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [companyId, scopeFilter]);

  const fetchCatalog = useCallback(async () => {
    try {
      const res = await fetch(`${API}/roles-permissions/catalog`, { headers: getAuthHeaders() });
      setCatalog(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  const fetchDepartments = useCallback(async () => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/hr-departments`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setDepartments(Array.isArray(data) ? data : (data.data || []));
      }
    } catch (e) { /* departments endpoint may not exist under this exact path — non-fatal */ }
  }, [companyId]);

  useEffect(() => { fetchGrants(); }, [fetchGrants]);
  useEffect(() => { fetchCatalog(); fetchDepartments(); }, [fetchCatalog, fetchDepartments]);

  const selectedModule = catalog.find((m) => m.module_key === form.module_key);
  const selectedSub = selectedModule?.submodules.find((s) => s.submodule_name === form.submodule_name);

  const addGrant = async () => {
    if (form.scope_type === 'department' && !form.scope_id) { alert('Select a department'); return; }
    if ((form.scope_type === 'module' || form.scope_type === 'submodule') && !form.module_key) { alert('Select a module'); return; }
    if (form.scope_type === 'submodule' && !form.submodule_name) { alert('Select a submodule'); return; }
    try {
      const res = await fetch(`${API}/companies/${companyId}/roles-permissions/grants`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          scope_type: form.scope_type,
          scope_id: form.scope_type === 'department' ? Number(form.scope_id) : null,
          module_key: ['module', 'submodule'].includes(form.scope_type) ? form.module_key : null,
          submodule_name: form.scope_type === 'submodule' ? form.submodule_name : null,
          permission_id: form.permission_id ? Number(form.permission_id) : null,
          state: form.state,
          reason: form.reason || null,
          is_temporary: form.is_temporary,
          starts_at: form.is_temporary ? (form.starts_at || null) : null,
          ends_at: form.is_temporary ? (form.ends_at || null) : null,
        }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.message || 'Failed'); }
      setShowAdd(false);
      setForm({ scope_type: 'company', scope_id: '', module_key: '', submodule_name: '', permission_id: '', state: 'grant', reason: '', is_temporary: false, starts_at: '', ends_at: '' });
      fetchGrants();
    } catch (e) { alert(e.message); }
  };

  const revoke = async (id) => {
    if (!window.confirm('Revoke this grant?')) return;
    try {
      await fetch(`${API}/companies/${companyId}/roles-permissions/grants/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
      fetchGrants();
    } catch (e) { console.error(e); }
  };

  if (initialLoading) return <div style={{ padding: 32, textAlign: 'center' }}>Loading...</div>;

  return (
    <div style={{ padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>🏢 Company, Module & Submodule Permission Management</h2>
        <button style={btnPrimary} onClick={() => setShowAdd(true)}>+ New Grant</button>
      </div>

      <div style={{ marginBottom: 12 }}>
        <select style={{ ...inputStyle, width: 220 }} value={scopeFilter} onChange={(e) => setScopeFilter(e.target.value)}>
          <option value="">All scopes</option>
          <option value="company">Company</option>
          <option value="department">Department</option>
          <option value="module">Module</option>
          <option value="submodule">Submodule</option>
        </select>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 18 }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Scope</th>
                <th style={thStyle}>Module / Submodule</th>
                <th style={thStyle}>Permission</th>
                <th style={thStyle}>State</th>
                <th style={thStyle}>Temporary</th>
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {grants.map((g) => (
                <tr key={g.id}>
                  <td style={tdStyle}><Badge value={g.scope_type} /> {g.scope_type === 'department' ? `#${g.scope_id}` : ''}</td>
                  <td style={tdStyle}>{g.module_key || '—'}{g.submodule_name ? ` / ${g.submodule_name}` : ''}</td>
                  <td style={tdStyle}>{g.permission?.name || 'All (blanket)'}</td>
                  <td style={tdStyle}><Badge value={g.state} /></td>
                  <td style={tdStyle}>{g.is_temporary ? <Badge value={g.status} /> : 'No'}</td>
                  <td style={tdStyle}><button style={btnDanger} onClick={() => revoke(g.id)}>Revoke</button></td>
                </tr>
              ))}
              {grants.length === 0 && <tr><td style={tdStyle} colSpan={6}>No grants at this scope yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {showAdd && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 14 }}>New Scope Grant</div>
            <FormRow label="Scope">
              <select style={inputStyle} value={form.scope_type} onChange={(e) => setForm({ ...form, scope_type: e.target.value, scope_id: '', module_key: '', submodule_name: '', permission_id: '' })}>
                {Object.entries(SCOPE_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </FormRow>
            {form.scope_type === 'department' && (
              <FormRow label="Department">
                <select style={inputStyle} value={form.scope_id} onChange={(e) => setForm({ ...form, scope_id: e.target.value })}>
                  <option value="">Select department...</option>
                  {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </FormRow>
            )}
            {(form.scope_type === 'module' || form.scope_type === 'submodule') && (
              <FormRow label="Module">
                <select style={inputStyle} value={form.module_key} onChange={(e) => setForm({ ...form, module_key: e.target.value, submodule_name: '', permission_id: '' })}>
                  <option value="">Select module...</option>
                  {catalog.map((m) => <option key={m.module_key} value={m.module_key}>{m.module_name}</option>)}
                </select>
              </FormRow>
            )}
            {form.scope_type === 'submodule' && selectedModule && (
              <FormRow label="Submodule">
                <select style={inputStyle} value={form.submodule_name} onChange={(e) => setForm({ ...form, submodule_name: e.target.value, permission_id: '' })}>
                  <option value="">Select submodule...</option>
                  {selectedModule.submodules.map((s) => <option key={s.submodule_name} value={s.submodule_name}>{s.submodule_name}</option>)}
                </select>
              </FormRow>
            )}
            {(form.scope_type === 'module' || form.scope_type === 'submodule') && (
              <FormRow label="Specific Permission (optional — blank = all in scope)">
                <select style={inputStyle} value={form.permission_id} onChange={(e) => setForm({ ...form, permission_id: e.target.value })}>
                  <option value="">All permissions in scope</option>
                  {form.scope_type === 'submodule' && selectedSub && selectedSub.permissions.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  {form.scope_type === 'module' && selectedModule && selectedModule.submodules.flatMap((s) => s.permissions).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </FormRow>
            )}
            {(form.scope_type === 'company' || form.scope_type === 'department') && (
              <FormRow label="Specific Permission (optional — blank = all permissions)">
                <select style={inputStyle} value={form.permission_id} onChange={(e) => setForm({ ...form, permission_id: e.target.value })}>
                  <option value="">All permissions</option>
                  {catalog.flatMap((m) => m.submodules.flatMap((s) => s.permissions)).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </FormRow>
            )}
            <FormRow label="State">
              <select style={inputStyle} value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })}>
                <option value="grant">Grant</option>
                <option value="remove">Remove (explicit denial)</option>
              </select>
            </FormRow>
            <FormRow label="Reason">
              <input style={inputStyle} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
            </FormRow>
            <FormRow label="Temporary / Timed">
              <label style={{ fontSize: 13 }}>
                <input type="checkbox" checked={form.is_temporary} onChange={(e) => setForm({ ...form, is_temporary: e.target.checked })} /> Make this a timed grant
              </label>
            </FormRow>
            {form.is_temporary && (
              <>
                <FormRow label="Starts At">
                  <input type="datetime-local" style={inputStyle} value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} />
                </FormRow>
                <FormRow label="Ends At">
                  <input type="datetime-local" style={inputStyle} value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} />
                </FormRow>
              </>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
              <button style={btnSecondary} onClick={() => setShowAdd(false)}>Cancel</button>
              <button style={btnPrimary} onClick={addGrant}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
